/**
 * How the shell is shaped, per device.
 *
 * The sidebar is **dragged, not configured**. An earlier version of this file
 * offered a Navigation setting with three modes; Notion and Linear both prove
 * the setting is unnecessary — you grab the edge and pull, and ⌘\ collapses it.
 * Direct manipulation beats a settings screen for anything the user can see
 * while they change it, and it deletes a whole section of Settings.
 *
 * Density stays a setting because there is nothing to grab: it changes type and
 * spacing across every screen at once. It remains pure CSS custom properties —
 * the rule is unchanged, an option is affordable when it lives in tokens.
 *
 * Stored per device rather than on the user, because the right answer differs
 * between a 27" monitor and a 13" laptop — the same reason the theme lives in
 * localStorage.
 */

export type Density = 'comfortable' | 'compact'

export interface LayoutPreferences {
  density: Density
  /** Pixels: either the rail width, or a value in [SIDEBAR_MIN, SIDEBAR_MAX]. */
  sidebarWidth: number
  /**
   * The width collapsing came *from*, so expanding restores the size the user
   * had chosen rather than a default they never picked.
   */
  sidebarRestoreWidth: number
}

/** Narrow enough that labels stop fitting; the rail takes over below this. */
export const SIDEBAR_RAIL_WIDTH = 56
export const SIDEBAR_SNAP_WIDTH = 150
export const SIDEBAR_MIN_WIDTH = 200
export const SIDEBAR_MAX_WIDTH = 420
export const SIDEBAR_DEFAULT_WIDTH = 256

export const LAYOUT_DEFAULTS: LayoutPreferences = {
  density: 'comfortable',
  sidebarWidth: SIDEBAR_DEFAULT_WIDTH,
  sidebarRestoreWidth: SIDEBAR_DEFAULT_WIDTH,
}

/** Shared with the inline script in `index.html`, which must not import anything. */
export const LAYOUT_STORAGE_KEY = 'blitztask.layout'

/**
 * Dragging below the snap point parks the sidebar as an icon rail rather than
 * squeezing labels into 90px. Linear's behaviour, and it means the rail needs
 * no setting of its own — it is just a width.
 */
export function resolveSidebarWidth(width: number): number {
  if (width < SIDEBAR_SNAP_WIDTH) return SIDEBAR_RAIL_WIDTH
  return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, width))
}

export function isRailWidth(width: number): boolean {
  return resolveSidebarWidth(width) === SIDEBAR_RAIL_WIDTH
}

/** Tolerant of anything: a stale or hand-edited value falls back rather than throwing. */
export function parseLayoutPreferences(raw: string | null): LayoutPreferences {
  if (!raw) return { ...LAYOUT_DEFAULTS }

  try {
    const parsed = JSON.parse(raw) as Partial<LayoutPreferences>
    return {
      density: parsed.density === 'compact' ? 'compact' : 'comfortable',
      sidebarWidth:
        typeof parsed.sidebarWidth === 'number' && Number.isFinite(parsed.sidebarWidth)
          ? resolveSidebarWidth(parsed.sidebarWidth)
          : LAYOUT_DEFAULTS.sidebarWidth,
      sidebarRestoreWidth:
        typeof parsed.sidebarRestoreWidth === 'number' &&
        Number.isFinite(parsed.sidebarRestoreWidth)
          ? Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, parsed.sidebarRestoreWidth))
          : LAYOUT_DEFAULTS.sidebarRestoreWidth,
    }
  } catch {
    return { ...LAYOUT_DEFAULTS }
  }
}

/** Writes what the stylesheet keys off. Mirrored by the inline script in index.html. */
export function applyLayoutPreferences(preferences: LayoutPreferences): void {
  const root = document.documentElement
  root.dataset.density = preferences.density
  root.style.setProperty('--sidebar-w', `${preferences.sidebarWidth}px`)
}
